import React, { useState } from "react";

import SignatureField from "../common/SignatureField"; // adjust path if needed

export default function StaffRegistrationFormFull({ onSubmit, initialData }) {
  const [formData, setFormData] = useState(
    initialData || {
      // Personal details
      fullName: "",
      previousNames: "",
      dob: "",
      addressLine1: "",
      addressLine2: "",
      postcode: "",
      phone: "",
      email: "",
      niNumber: "",
      nationality: "",

      // Right to work
      rightToWorkStatus: "",
      rtWorkShareCode: "",
      passportNumber: "",
      visaExpiry: "",

      // Employment history (last 5 years – summary style)
      currentRole: "",
      currentEmployer: "",
      currentEmployerFrom: "",
      currentEmployerTo: "",
      employmentHistorySummary: "",
      employmentGapsExplanation: "",

      // Qualifications & registrations
      qualifications: "",
      professionalRegistrationBody: "",
      professionalRegistrationNumber: "",
      professionalRegistrationExpiry: "",

      // References
      ref1Name: "",
      ref1Relationship: "",
      ref1Email: "",
      ref1Phone: "",
      ref2Name: "",
      ref2Relationship: "",
      ref2Email: "",
      ref2Phone: "",

      // Safeguarding / DBS / convictions
      hasCriminalConvictions: "",
      criminalConvictionDetails: "",
      hasDBS: "",
      dbsNumber: "",
      dbsIssueDate: "",
      dbsUpdateService: "",
      barredListStatus: "",

      // Health & fitness for work
      fitToWork: "",
      healthConditionsAffectingWork: "",
      infectionsOrBackIssues: "",
      onMedication: "",
      healthNotes: "",

      // Availability
      roleAppliedFor: "",
      preferredLocations: "",
      daysAvailable: "",
      hoursAvailable: "",

      // Data protection & declaration
      consentContactRefereesBeforeOffer: "",
      consentContactRefereesAfterOffer: "yes",
      gdprConsent: false,
      declarationAccepted: false,
      declarationName: "",
      declarationDate: "",
    }
  );

  const [errors, setErrors] = useState({});

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.fullName.trim()) newErrors.fullName = "Required";
    if (!formData.dob) newErrors.dob = "Required";
    if (!formData.addressLine1.trim()) newErrors.addressLine1 = "Required";
    if (!formData.postcode.trim()) newErrors.postcode = "Required";
    if (!formData.phone.trim()) newErrors.phone = "Required";
    if (!formData.email.trim()) newErrors.email = "Required";
    if (!formData.roleAppliedFor.trim()) newErrors.roleAppliedFor = "Required";
    if (!formData.ref1Name.trim()) newErrors.ref1Name = "Required";
    if (!formData.ref1Email.trim()) newErrors.ref1Email = "Required";
    if (!formData.fitToWork) newErrors.fitToWork = "Please confirm fitness to work";
    if (!formData.hasCriminalConvictions)
      newErrors.hasCriminalConvictions = "Required";
    if (!formData.gdprConsent)
      newErrors.gdprConsent = "You must consent to data processing";
    if (!formData.declarationAccepted)
      newErrors.declarationAccepted = "You must confirm the declaration";
    if (!formData.declarationName.trim())
      newErrors.declarationName = "Please type your full name";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    if (typeof onSubmit === "function") {
      onSubmit(formData);
    } else {
      console.log("Staff Registration (FULL) data:", formData);
      alert("Form submitted (check console). Wire onSubmit to save to Firestore.");
    }
  };

  const sectionTitle = {
    fontWeight: 600,
    marginTop: 16,
    marginBottom: 8,
    fontSize: 14,
    color: "#111827",
  };

  const labelStyle = {
    fontSize: 13,
    color: "#374151",
    marginBottom: 4,
    display: "block",
  };

  const inputStyle = {
    width: "100%",
    padding: "8px 10px",
    borderRadius: 8,
    border: "1px solid #d1d5db",
    fontSize: 13,
    outline: "none",
  };

  const errorStyle = {
    fontSize: 12,
    color: "#b91c1c",
    marginTop: 2,
  };

  const radioRow = {
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: 13,
    marginRight: 16,
  };

  const textAreaStyle = {
    ...inputStyle,
    minHeight: 70,
    resize: "vertical",
  };

  return (
    <form onSubmit={handleSubmit}>
      <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 4 }}>
        Staffing Registration Form (Full)
      </h3>
      <p style={{ fontSize: 12, color: "#6b7280", marginBottom: 16 }}>
        This form supports safer recruitment and UK HR / CQC requirements. Please
        complete all sections as fully and accurately as possible.
      </p>

      {/* 1. Personal Details */}
      <div style={sectionTitle}>1. Personal Details</div>

      <div style={{ marginBottom: 10 }}>
        <label style={labelStyle}>Full Name *</label>
        <input
          style={inputStyle}
          value={formData.fullName}
          onChange={(e) => handleChange("fullName", e.target.value)}
        />
        {errors.fullName && <div style={errorStyle}>{errors.fullName}</div>}
      </div>

      <div style={{ marginBottom: 10 }}>
        <label style={labelStyle}>Any Previous Names (e.g. maiden name)</label>
        <input
          style={inputStyle}
          value={formData.previousNames}
          onChange={(e) => handleChange("previousNames", e.target.value)}
        />
      </div>

      <div style={{ display: "flex", gap: 10 }}>
        <div style={{ flex: 1, marginBottom: 10 }}>
          <label style={labelStyle}>Date of Birth *</label>
          <input
            type="date"
            style={inputStyle}
            value={formData.dob}
            onChange={(e) => handleChange("dob", e.target.value)}
          />
          {errors.dob && <div style={errorStyle}>{errors.dob}</div>}
        </div>
        <div style={{ flex: 1, marginBottom: 10 }}>
          <label style={labelStyle}>Nationality</label>
          <input
            style={inputStyle}
            value={formData.nationality}
            onChange={(e) => handleChange("nationality", e.target.value)}
          />
        </div>
      </div>

      <div style={{ marginBottom: 10 }}>
        <label style={labelStyle}>Address Line 1 *</label>
        <input
          style={inputStyle}
          value={formData.addressLine1}
          onChange={(e) => handleChange("addressLine1", e.target.value)}
        />
        {errors.addressLine1 && (
          <div style={errorStyle}>{errors.addressLine1}</div>
        )}
      </div>

      <div style={{ marginBottom: 10 }}>
        <label style={labelStyle}>Address Line 2</label>
        <input
          style={inputStyle}
          value={formData.addressLine2}
          onChange={(e) => handleChange("addressLine2", e.target.value)}
        />
      </div>

      <div style={{ display: "flex", gap: 10 }}>
        <div style={{ flex: 1, marginBottom: 10 }}>
          <label style={labelStyle}>Postcode *</label>
          <input
            style={inputStyle}
            value={formData.postcode}
            onChange={(e) => handleChange("postcode", e.target.value)}
          />
          {errors.postcode && (
            <div style={errorStyle}>{errors.postcode}</div>
          )}
        </div>
        <div style={{ flex: 1, marginBottom: 10 }}>
          <label style={labelStyle}>Phone Number *</label>
          <input
            style={inputStyle}
            value={formData.phone}
            onChange={(e) => handleChange("phone", e.target.value)}
          />
          {errors.phone && <div style={errorStyle}>{errors.phone}</div>}
        </div>
      </div>

      <div style={{ marginBottom: 10 }}>
        <label style={labelStyle}>Email *</label>
        <input
          style={inputStyle}
          type="email"
          value={formData.email}
          onChange={(e) => handleChange("email", e.target.value)}
        />
        {errors.email && <div style={errorStyle}>{errors.email}</div>}
      </div>

      <div style={{ marginBottom: 10 }}>
        <label style={labelStyle}>National Insurance Number</label>
        <input
          style={inputStyle}
          value={formData.niNumber}
          onChange={(e) => handleChange("niNumber", e.target.value)}
        />
      </div>

      {/* 2. Right to Work */}
      <div style={sectionTitle}>2. Right to Work in the UK</div>
      <p style={{ fontSize: 12, color: "#4b5563", marginBottom: 4 }}>
        You will be required to provide original documents or a Home Office
        online share code to evidence your right to work.
      </p>

      <div style={{ marginBottom: 8 }}>
        <span style={labelStyle}>Right to work status</span>
        <select
          style={inputStyle}
          value={formData.rightToWorkStatus}
          onChange={(e) =>
            handleChange("rightToWorkStatus", e.target.value)
          }
        >
          <option value="">Select...</option>
          <option value="UK citizen">UK citizen</option>
          <option value="EU settled / pre-settled">
            EU settled / pre-settled status
          </option>
          <option value="Visa holder">Visa holder</option>
          <option value="Other">Other</option>
        </select>
      </div>

      <div style={{ marginBottom: 10 }}>
        <label style={labelStyle}>Right to Work Share Code (if applicable)</label>
        <input
          style={inputStyle}
          value={formData.rtWorkShareCode}
          onChange={(e) => handleChange("rtWorkShareCode", e.target.value)}
        />
      </div>

      <div style={{ display: "flex", gap: 10 }}>
        <div style={{ flex: 1, marginBottom: 10 }}>
          <label style={labelStyle}>Passport Number (if applicable)</label>
          <input
            style={inputStyle}
            value={formData.passportNumber}
            onChange={(e) =>
              handleChange("passportNumber", e.target.value)
            }
          />
        </div>
        <div style={{ flex: 1, marginBottom: 10 }}>
          <label style={labelStyle}>Visa / BRP Expiry Date</label>
          <input
            type="date"
            style={inputStyle}
            value={formData.visaExpiry}
            onChange={(e) => handleChange("visaExpiry", e.target.value)}
          />
        </div>
      </div>

      {/* 3. Employment History */}
      <div style={sectionTitle}>3. Employment History (Last 5 Years)</div>
      <p style={{ fontSize: 12, color: "#4b5563", marginBottom: 4 }}>
        Please provide details of your current or most recent role and a summary
        of your employment for the last 5 years. Explain any gaps in employment.
      </p>

      <div style={{ marginBottom: 10 }}>
        <label style={labelStyle}>Current / Most Recent Job Title</label>
        <input
          style={inputStyle}
          value={formData.currentRole}
          onChange={(e) => handleChange("currentRole", e.target.value)}
        />
      </div>

      <div style={{ marginBottom: 10 }}>
        <label style={labelStyle}>Current / Most Recent Employer</label>
        <input
          style={inputStyle}
          value={formData.currentEmployer}
          onChange={(e) =>
            handleChange("currentEmployer", e.target.value)
          }
        />
      </div>

      <div style={{ display: "flex", gap: 10 }}>
        <div style={{ flex: 1, marginBottom: 10 }}>
          <label style={labelStyle}>From (month/year)</label>
          <input
            style={inputStyle}
            placeholder="MM/YYYY"
            value={formData.currentEmployerFrom}
            onChange={(e) =>
              handleChange("currentEmployerFrom", e.target.value)
            }
          />
        </div>
        <div style={{ flex: 1, marginBottom: 10 }}>
          <label style={labelStyle}>To (month/year or 'Present')</label>
          <input
            style={inputStyle}
            placeholder="MM/YYYY or Present"
            value={formData.currentEmployerTo}
            onChange={(e) =>
              handleChange("currentEmployerTo", e.target.value)
            }
          />
        </div>
      </div>

      <div style={{ marginBottom: 10 }}>
        <label style={labelStyle}>
          Summary of employment for the last 5 years
        </label>
        <textarea
          style={textAreaStyle}
          value={formData.employmentHistorySummary}
          onChange={(e) =>
            handleChange("employmentHistorySummary", e.target.value)
          }
        />
      </div>

      <div style={{ marginBottom: 10 }}>
        <label style={labelStyle}>
          Explanation of any gaps in employment (over 1 month)
        </label>
        <textarea
          style={textAreaStyle}
          value={formData.employmentGapsExplanation}
          onChange={(e) =>
            handleChange("employmentGapsExplanation", e.target.value)
          }
        />
      </div>

      {/* 4. Qualifications & Registration */}
      <div style={sectionTitle}>4. Qualifications & Professional Registration</div>

      <div style={{ marginBottom: 10 }}>
        <label style={labelStyle}>
          Relevant qualifications / training (with dates)
        </label>
        <textarea
          style={textAreaStyle}
          value={formData.qualifications}
          onChange={(e) => handleChange("qualifications", e.target.value)}
        />
      </div>

      <div style={{ marginBottom: 10 }}>
        <label style={labelStyle}>
          Professional registration body (e.g. NMC, HCPC) if applicable
        </label>
        <input
          style={inputStyle}
          value={formData.professionalRegistrationBody}
          onChange={(e) =>
            handleChange("professionalRegistrationBody", e.target.value)
          }
        />
      </div>

      <div style={{ display: "flex", gap: 10 }}>
        <div style={{ flex: 1, marginBottom: 10 }}>
          <label style={labelStyle}>Registration Number</label>
          <input
            style={inputStyle}
            value={formData.professionalRegistrationNumber}
            onChange={(e) =>
              handleChange(
                "professionalRegistrationNumber",
                e.target.value
              )
            }
          />
        </div>
        <div style={{ flex: 1, marginBottom: 10 }}>
          <label style={labelStyle}>Registration Expiry Date</label>
          <input
            type="date"
            style={inputStyle}
            value={formData.professionalRegistrationExpiry}
            onChange={(e) =>
              handleChange(
                "professionalRegistrationExpiry",
                e.target.value
              )
            }
          />
        </div>
      </div>

      {/* 5. References */}
      <div style={sectionTitle}>5. References</div>
      <p style={{ fontSize: 12, color: "#4b5563", marginBottom: 4 }}>
        Please provide details of at least two referees, one of whom should be
        your current or most recent employer. We will not normally contact
        referees without your consent.
      </p>

      <div style={{ marginBottom: 8, fontWeight: 500, fontSize: 13 }}>
        Reference 1 (current / most recent employer) *
      </div>

      <div style={{ marginBottom: 10 }}>
        <label style={labelStyle}>Name *</label>
        <input
          style={inputStyle}
          value={formData.ref1Name}
          onChange={(e) => handleChange("ref1Name", e.target.value)}
        />
        {errors.ref1Name && <div style={errorStyle}>{errors.ref1Name}</div>}
      </div>
      <div style={{ marginBottom: 10 }}>
        <label style={labelStyle}>Relationship / Position</label>
        <input
          style={inputStyle}
          value={formData.ref1Relationship}
          onChange={(e) =>
            handleChange("ref1Relationship", e.target.value)
          }
        />
      </div>
      <div style={{ display: "flex", gap: 10 }}>
        <div style={{ flex: 1, marginBottom: 10 }}>
          <label style={labelStyle}>Email *</label>
          <input
            style={inputStyle}
            value={formData.ref1Email}
            onChange={(e) => handleChange("ref1Email", e.target.value)}
          />
          {errors.ref1Email && (
            <div style={errorStyle}>{errors.ref1Email}</div>
          )}
        </div>
        <div style={{ flex: 1, marginBottom: 10 }}>
          <label style={labelStyle}>Phone</label>
          <input
            style={inputStyle}
            value={formData.ref1Phone}
            onChange={(e) => handleChange("ref1Phone", e.target.value)}
          />
        </div>
      </div>

      <div style={{ marginBottom: 8, fontWeight: 500, fontSize: 13 }}>
        Reference 2
      </div>

      <div style={{ marginBottom: 10 }}>
        <label style={labelStyle}>Name</label>
        <input
          style={inputStyle}
          value={formData.ref2Name}
          onChange={(e) => handleChange("ref2Name", e.target.value)}
        />
      </div>
      <div style={{ marginBottom: 10 }}>
        <label style={labelStyle}>Relationship / Position</label>
        <input
          style={inputStyle}
          value={formData.ref2Relationship}
          onChange={(e) =>
            handleChange("ref2Relationship", e.target.value)
          }
        />
      </div>
      <div style={{ display: "flex", gap: 10 }}>
        <div style={{ flex: 1, marginBottom: 10 }}>
          <label style={labelStyle}>Email</label>
          <input
            style={inputStyle}
            value={formData.ref2Email}
            onChange={(e) => handleChange("ref2Email", e.target.value)}
          />
        </div>
        <div style={{ flex: 1, marginBottom: 10 }}>
          <label style={labelStyle}>Phone</label>
          <input
            style={inputStyle}
            value={formData.ref2Phone}
            onChange={(e) => handleChange("ref2Phone", e.target.value)}
          />
        </div>
      </div>

      {/* 6. Safeguarding / DBS */}
      <div style={sectionTitle}>6. Safeguarding & Criminal Records</div>

      <div style={{ marginBottom: 8 }}>
        <span style={labelStyle}>
          Do you have any unspent criminal convictions, cautions or bind-overs?
        </span>
        <div style={{ display: "flex" }}>
          <label style={radioRow}>
            <input
              type="radio"
              name="hasCriminalConvictions"
              value="yes"
              checked={formData.hasCriminalConvictions === "yes"}
              onChange={(e) =>
                handleChange("hasCriminalConvictions", e.target.value)
              }
            />
            Yes
          </label>
          <label style={radioRow}>
            <input
              type="radio"
              name="hasCriminalConvictions"
              value="no"
              checked={formData.hasCriminalConvictions === "no"}
              onChange={(e) =>
                handleChange("hasCriminalConvictions", e.target.value)
              }
            />
            No
          </label>
        </div>
        {errors.hasCriminalConvictions && (
          <div style={errorStyle}>{errors.hasCriminalConvictions}</div>
        )}
      </div>

      {formData.hasCriminalConvictions === "yes" && (
        <div style={{ marginBottom: 10 }}>
          <label style={labelStyle}>Please give details</label>
          <textarea
            style={textAreaStyle}
            value={formData.criminalConvictionDetails}
            onChange={(e) =>
              handleChange("criminalConvictionDetails", e.target.value)
            }
          />
        </div>
      )}

      <div style={{ marginBottom: 8 }}>
        <span style={labelStyle}>Do you currently hold a DBS certificate?</span>
        <div style={{ display: "flex" }}>
          <label style={radioRow}>
            <input
              type="radio"
              name="hasDBS"
              value="yes"
              checked={formData.hasDBS === "yes"}
              onChange={(e) => handleChange("hasDBS", e.target.value)}
            />
            Yes
          </label>
          <label style={radioRow}>
            <input
              type="radio"
              name="hasDBS"
              value="no"
              checked={formData.hasDBS === "no"}
              onChange={(e) => handleChange("hasDBS", e.target.value)}
            />
            No
          </label>
        </div>
      </div>

      {formData.hasDBS === "yes" && (
        <>
          <div style={{ marginBottom: 10 }}>
            <label style={labelStyle}>DBS Certificate Number</label>
            <input
              style={inputStyle}
              value={formData.dbsNumber}
              onChange={(e) => handleChange("dbsNumber", e.target.value)}
            />
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <div style={{ flex: 1, marginBottom: 10 }}>
              <label style={labelStyle}>DBS Issue Date</label>
              <input
                type="date"
                style={inputStyle}
                value={formData.dbsIssueDate}
                onChange={(e) =>
                  handleChange("dbsIssueDate", e.target.value)
                }
              />
            </div>
            <div style={{ flex: 1, marginBottom: 10 }}>
              <label style={labelStyle}>Registered with Update Service?</label>
              <select
                style={inputStyle}
                value={formData.dbsUpdateService}
                onChange={(e) =>
                  handleChange("dbsUpdateService", e.target.value)
                }
              >
                <option value="">Select...</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </div>
          </div>
        </>
      )}

      <div style={{ marginBottom: 10 }}>
        <label style={labelStyle}>
          Are you barred from working with vulnerable adults or children?
        </label>
        <input
          style={inputStyle}
          value={formData.barredListStatus}
          onChange={(e) =>
            handleChange("barredListStatus", e.target.value)
          }
        />
      </div>

      {/* 7. Health */}
      <div style={sectionTitle}>7. Health & Fitness for Work</div>

      <div style={{ marginBottom: 8 }}>
        <span style={labelStyle}>
          Are you fit to carry out the duties of the role you have applied for?
        </span>
        <div style={{ display: "flex" }}>
          <label style={radioRow}>
            <input
              type="radio"
              name="fitToWork"
              value="yes"
              checked={formData.fitToWork === "yes"}
              onChange={(e) => handleChange("fitToWork", e.target.value)}
            />
            Yes
          </label>
          <label style={radioRow}>
            <input
              type="radio"
              name="fitToWork"
              value="no"
              checked={formData.fitToWork === "no"}
              onChange={(e) => handleChange("fitToWork", e.target.value)}
            />
            No
          </label>
        </div>
        {errors.fitToWork && <div style={errorStyle}>{errors.fitToWork}</div>}
      </div>

      <div style={{ marginBottom: 10 }}>
        <label style={labelStyle}>
          Any health conditions we should be aware of that may affect your work?
        </label>
        <textarea
          style={textAreaStyle}
          value={formData.healthConditionsAffectingWork}
          onChange={(e) =>
            handleChange(
              "healthConditionsAffectingWork",
              e.target.value
            )
          }
        />
      </div>

      <div style={{ marginBottom: 10 }}>
        <label style={labelStyle}>
          Any history of back problems, injuries or infectious diseases?
        </label>
        <textarea
          style={textAreaStyle}
          value={formData.infectionsOrBackIssues}
          onChange={(e) =>
            handleChange("infectionsOrBackIssues", e.target.value)
          }
        />
      </div>

      <div style={{ marginBottom: 10 }}>
        <label style={labelStyle}>
          Are you currently taking any medication that may affect your work?
        </label>
        <textarea
          style={textAreaStyle}
          value={formData.onMedication}
          onChange={(e) => handleChange("onMedication", e.target.value)}
        />
      </div>

      <div style={{ marginBottom: 10 }}>
        <label style={labelStyle}>Any other information about your health</label>
        <textarea
          style={textAreaStyle}
          value={formData.healthNotes}
          onChange={(e) => handleChange("healthNotes", e.target.value)}
        />
      </div>

      {/* 8. Availability */}
      <div style={sectionTitle}>8. Role & Availability</div>

      <div style={{ marginBottom: 10 }}>
        <label style={labelStyle}>Role applied for *</label>
        <input
          style={inputStyle}
          value={formData.roleAppliedFor}
          onChange={(e) =>
            handleChange("roleAppliedFor", e.target.value)
          }
        />
        {errors.roleAppliedFor && (
          <div style={errorStyle}>{errors.roleAppliedFor}</div>
        )}
      </div>

      <div style={{ marginBottom: 10 }}>
        <label style={labelStyle}>Preferred locations / areas</label>
        <input
          style={inputStyle}
          value={formData.preferredLocations}
          onChange={(e) =>
            handleChange("preferredLocations", e.target.value)
          }
        />
      </div>

      <div style={{ marginBottom: 10 }}>
        <label style={labelStyle}>
          Days available (e.g. Mon–Fri, weekends, nights)
        </label>
        <input
          style={inputStyle}
          value={formData.daysAvailable}
          onChange={(e) =>
            handleChange("daysAvailable", e.target.value)
          }
        />
      </div>

      <div style={{ marginBottom: 10 }}>
        <label style={labelStyle}>
          Hours / shifts you are available (e.g. 8–8, nights, flexible)
        </label>
        <input
          style={inputStyle}
          value={formData.hoursAvailable}
          onChange={(e) =>
            handleChange("hoursAvailable", e.target.value)
          }
        />
      </div>
	  
	  <SignatureField
  label="Sign here (using your finger or mouse)"
  value={formData.signature}
  onChange={(val) => handleChange("signature", val)}
/>

	  
	  
	  

      {/* 9. Consent & Declaration */}
      <div style={sectionTitle}>9. Consent & Declaration</div>

      <p style={{ fontSize: 12, color: "#4b5563", marginBottom: 8 }}>
        By submitting this form you consent to the information provided being
        used for recruitment, safeguarding and HR purposes in line with our
        Privacy Notice and data protection law.
      </p>

      <div style={{ marginBottom: 8 }}>
        <span style={labelStyle}>
          May we contact your referees before a formal offer is made?
        </span>
        <div style={{ display: "flex" }}>
          <label style={radioRow}>
            <input
              type="radio"
              name="consentContactRefereesBeforeOffer"
              value="yes"
              checked={
                formData.consentContactRefereesBeforeOffer === "yes"
              }
              onChange={(e) =>
                handleChange(
                  "consentContactRefereesBeforeOffer",
                  e.target.value
                )
              }
            />
            Yes
          </label>
          <label style={radioRow}>
            <input
              type="radio"
              name="consentContactRefereesBeforeOffer"
              value="no"
              checked={
                formData.consentContactRefereesBeforeOffer === "no"
              }
              onChange={(e) =>
                handleChange(
                  "consentContactRefereesBeforeOffer",
                  e.target.value
                )
              }
            />
            No
          </label>
        </div>
      </div>

      <label style={{ ...radioRow, marginBottom: 4 }}>
        <input
          type="checkbox"
          checked={formData.gdprConsent}
          onChange={(e) =>
            handleChange("gdprConsent", e.target.checked)
          }
        />
        <span style={{ fontSize: 12 }}>
          I consent to my personal data being processed for recruitment and
          employment purposes in line with data protection law.*
        </span>
      </label>
      {errors.gdprConsent && (
        <div style={errorStyle}>{errors.gdprConsent}</div>
      )}

      <p style={{ fontSize: 12, color: "#4b5563", marginTop: 10 }}>
        I confirm that the information I have provided is true and complete to
        the best of my knowledge and I understand that giving false information
        may lead to the withdrawal of any offer or dismissal.
      </p>

      <label style={{ ...radioRow, marginBottom: 4 }}>
        <input
          type="checkbox"
          checked={formData.declarationAccepted}
          onChange={(e) =>
            handleChange("declarationAccepted", e.target.checked)
          }
        />
        <span style={{ fontSize: 12 }}>
          I confirm the above declaration.*
        </span>
      </label>
      {errors.declarationAccepted && (
        <div style={errorStyle}>{errors.declarationAccepted}</div>
      )}

      <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
        <div style={{ flex: 1, marginBottom: 10 }}>
          <label style={labelStyle}>Type your full name as signature *</label>
          <input
            style={inputStyle}
            value={formData.declarationName}
            onChange={(e) =>
              handleChange("declarationName", e.target.value)
            }
          />
          {errors.declarationName && (
            <div style={errorStyle}>{errors.declarationName}</div>
          )}
        </div>
        <div style={{ flex: 1, marginBottom: 10 }}>
          <label style={labelStyle}>Date</label>
          <input
            type="date"
            style={inputStyle}
            value={formData.declarationDate}
            onChange={(e) =>
              handleChange("declarationDate", e.target.value)
            }
          />
        </div>
      </div>

      <button
        type="submit"
        style={{
          marginTop: 16,
          width: "100%",
          padding: "10px 0",
          borderRadius: 9999,
          border: "none",
          fontSize: 14,
          fontWeight: 600,
          backgroundColor: "#0284c7",
          color: "white",
          cursor: "pointer",
        }}
      >
        Submit Full Registration
      </button>
    </form>
  );
}
